import { noAuthPaths } from '@/constants/path';
import { axios } from '@/plugins/axios';
import { endPoints } from '@/shared/types/api';
import { buildUser, ResponseUser, User } from '@/shared/types/entities/User';
import { useStore } from '@/store';
import { AUTH_ACTION } from '@/store/actions/actionTypes';
import { NavigationGuard, Route } from 'vue-router';

const authCheck = (user: User, to: Route) => {
  const isAdminPage = to.path.includes('admin');
  const isUserPage = to.path.includes('user');

  const isAuthenticated = user?.isAuthenticated;
  const isAdministrator = user?.isAdministrator;

  // 管理者画面: ユーザーが未認証または教員システムでログインしている
  const shouldRedirectToAdmin =
    isAdminPage && (!isAuthenticated || !isAdministrator);
  // 教員画面: ユーザーが未認証または管理者システムでログインしている
  const shouldRedirectToUser =
    isUserPage && (!isAuthenticated || isAdministrator);

  return {
    shouldRedirectToAdmin,
    shouldRedirectToUser,
  };
};

export const authMiddleware: NavigationGuard<Vue> = async (to, from, next) => {
  const { commit, getters } = useStore();

  const handleRedirect = (
    shouldRedirectToAdmin: boolean,
    shouldRedirectToUser: boolean,
    user: User,
  ) => {
    const redirect = (toPath: '/admin' | '/user') => {
      next({ path: toPath, query: { redirect: to.fullPath } });
    };
    const isLoginPage = noAuthPaths.includes(to.path);

    if (isLoginPage) {
      // 認証済みの場合は、各ホーム画面へ遷移
      if (user.isAuthenticated) {
        if (
          user.isAdministrator &&
          (to.path === '/admin' || to.path === '/admin/')
        ) {
          next('/admin/result-publication/schools');
        } else if (
          !user.isAdministrator &&
          (to.path === '/user' || to.path === '/user/')
        ) {
          next('/user/result-publication/rehearsal');
        } else {
          next();
        }
      } else {
        next();
      }
    } else {
      // 未ログイン時または認証が切れている場合、ログイン画面へ遷移する
      if (shouldRedirectToAdmin) {
        redirect('/admin');
      } else if (shouldRedirectToUser) {
        redirect('/user');
      } else {
        next();
      }
    }
  };

  if (getters.user) {
    const { shouldRedirectToAdmin, shouldRedirectToUser } = authCheck(
      getters.user,
      to,
    );

    handleRedirect(shouldRedirectToAdmin, shouldRedirectToUser, getters.user);
  } else {
    const res: { data: { items: ResponseUser } } = await axios.get(
      endPoints.getUserInfo.endPoint,
    );
    const user = buildUser(res.data.items);
    commit(AUTH_ACTION.GET_USER, res.data.items);

    const { shouldRedirectToAdmin, shouldRedirectToUser } = authCheck(user, to);

    handleRedirect(shouldRedirectToAdmin, shouldRedirectToUser, user);
  }
};
